<template lang="pug">
  div.yz-file-preview
    yz-card.d-flex.pa-2.round-xl.mb-2(
      v-for="file in value"
      :key="file.fileId || file.hid"
      outlined
    )
      // Thumbnail/icon area
      .flex-grow-0.mr-4
        yz-img.round-md(
          v-if="isImage(file)"
          :width="thumbnailSize"
          :height="thumbnailSize"
          :src="getImageSrc(file)"
          cover
        )
        .yz-file-preview__pdf-thumbnail.round-md(
          v-else-if="isPdf(file)"
          :style="thumbnailContainerStyle"
          :ref="`pdfThumbnail_${file.fileId || getFileKey(file)}`"
        )
        .yz-file-preview__icon-container.round-md.d-flex.align-center.justify-center(
          v-else
          :style="thumbnailContainerStyle"
        )
          yz-icon(:size="iconSize" color="neutral-500") {{ getFileIcon(file) }}

      // File info
      .text-sm.flex-grow-1.d-flex.flex-column.justify-center
        .font-medium {{ formatFilename(file.filename) }}
        .yz-regular-body-xs.neutral500--text
          span {{ formatFileSize(file.size) }}
          span(v-if="showProgress && file.uploading && file.progress !== undefined")
            | &nbsp;- {{ file.progress }}% {{ $t ? $t('uploaded') : 'uploaded' }}

      // Action buttons
      .flex-grow-0.d-flex.align-center.ml-2(v-if="showActions && !file.uploading")
        yz-btn(
          v-if="allowDelete"
          icon="$yzi-trash-03"
          size="sm"
          outlined
          color="danger"
          :loading="isFileDeleting(file)"
          @click="deleteFile(file)"
        )
        yz-btn.ml-2(
          label="View"
          size="sm"
          outlined,
          unset-min-width,
          @click="viewFile(file)"
        )

      // Progress indicator
      .flex-grow-0.d-flex.align-center(v-if="showProgress && file.uploading && file.progress !== undefined")
        v-progress-circular(:size="22" :width="3" :value="file.progress" color="#0D6EFF")

    // Dialogs
    view-image-dialog(
      v-if="showViewImageDialog"
      :open.sync="showViewImageDialog"
      :image="selectedFile"
      @closed="selectedFile = null"
    )
    yz-delete-confirmation-dialog(
      v-if="showDeleteDialog"
      v-model="showDeleteDialog"
      item="file"
      description="This action will permanently remove the file and cannot be undone."
      :file-data="[fileToDelete]"
      :loading="isDeleting"
      @confirmed="confirmDelete"
      @canceled="cancelDelete"
    )
</template>

<script>
import { generatePdfThumbnail, formatFilename } from '@/utils'
import ViewImageDialog from '@/components/dialogs/ViewImageDialog.vue'
import YzDeleteConfirmationDialog from './YzDeleteConfirmationDialog.vue'
import { mapActions } from 'vuex'

export default {
  components: { ViewImageDialog, YzDeleteConfirmationDialog },
  name: 'YzFilePreview',
  props: {
    // v-model support
    value: {
      type: Array,
      default: () => []
    },
    // Thumbnail size
    size: {
      type: String,
      default: 'md',
      validator: value => ['sm', 'md', 'lg'].includes(value)
    },
    // Show upload progress
    showProgress: {
      type: Boolean,
      default: true
    },
    // Show action buttons
    showActions: {
      type: Boolean,
      default: true
    },
    // Allow delete action
    allowDelete: {
      type: Boolean,
      default: true
    },
    // Array to track items to be deleted
    toDeleteItems: {
      type: Array,
      default: () => []
    },
    // Delete strategy props
    deleteMode: {
      type: String,
      default: 'preview',
      validator: value => ['immediate', 'preview'].includes(value)
    },
    deleteUrl: {
      type: String,
      default: '/documents/'
    },
    deleteHeaders: {
      type: Object,
      default: () => ({})
    },
    deleteMethod: {
      type: String,
      default: 'DELETE'
    },
    // Filename truncation length
    truncateLength: {
      type: Number,
      default: 25
    }
  },
  data() {
    return {
      showViewImageDialog: false,
      selectedFile: null,
      isDeleting: false,
      showDeleteDialog: false,
      fileToDelete: null,
      deletingFiles: new Map() // Track delete progress for each file
    }
  },
  computed: {
    thumbnailSize() {
      const sizes = {
        sm: 32,
        md: 40,
        lg: 48
      }
      return sizes[this.size] || sizes.md
    },
    iconSize() {
      const sizes = {
        sm: 16,
        md: 18,
        lg: 20
      }
      return sizes[this.size] || sizes.md
    },
    thumbnailContainerStyle() {
      return {
        width: `${this.thumbnailSize}px`,
        height: `${this.thumbnailSize}px`,
        backgroundColor: 'var(--yz-neutral-200, #f5f5f5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }
    }
  },
  mounted() {
    // Generate PDF thumbnails for PDF files
    this.value.forEach(file => {
      if (this.isPdf(file)) {
        this.generatePdfThumbnailForFile(file)
      }
    })
  },
  watch: {
    value: {
      handler(newFiles) {
        // Generate thumbnails for new PDF files
        if (newFiles && Array.isArray(newFiles)) {
          newFiles.forEach(file => {
            if (this.isPdf(file) && !file.thumbnail) {
              this.$nextTick(() => {
                this.generatePdfThumbnailForFile(file)
              })
            }
          })
        }
      },
      deep: true
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    isFileDeleting(file) {
      const fileId = file.fileId || file.filename || file
      return this.deletingFiles.has(fileId) && this.deletingFiles.get(fileId).deleting
    },
    isImage(file) {
      if (!file) return false

      // Check if file has blob (preview mode)
      if (file.blob) {
        return file.type && file.type.startsWith('image/')
      }

      // Check file type
      if (file.type && file.type.startsWith('image/')) {
        return true
      }

      // Check filename extension for databin files
      if (file.filename) {
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg']
        const extension = '.' + file.filename.split('.').pop().toLowerCase()
        return imageExtensions.includes(extension)
      }

      return false
    },
    isPdf(file) {
      if (!file) return false

      // Check file type
      if (file.type === 'application/pdf') {
        return true
      }

      // Check filename extension
      if (file.filename) {
        return file.filename.toLowerCase().endsWith('.pdf')
      }

      return false
    },
    getImageSrc(file) {
      // Priority: blob > file URL > fallback
      if (file.blob) {
        return file.blob
      }

      if (typeof file.file === 'string') {
        return file.file
      }

      // If file.file is a File object, create blob URL
      if (file.file && file.file instanceof File) {
        return URL.createObjectURL(file.file)
      }

      return require('@/assets/image_placeholder.png')
    },
    getFileIcon(file) {
      if (!file || !file.filename) {
        return '$yzi-file-02'
      }

      const filename = file.filename.toLowerCase()
      const extension = filename.split('.').pop()

      // Document files
      if (['doc', 'docx'].includes(extension)) {
        return 'mdi-file-document-outline'
      }

      // Spreadsheet files
      if (['xls', 'xlsx', 'csv'].includes(extension)) {
        return 'mdi-file-excel'
      }

      // Presentation files
      if (['ppt', 'pptx'].includes(extension)) {
        return 'mdi-file-powerpoint'
      }

      // Archive files
      if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) {
        return 'mdi-folder-zip-outline'
      }

      // Video files
      if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension)) {
        return 'mdi-file-video'
      }

      // Audio files
      if (['mp3', 'wav', 'flac', 'aac', 'ogg'].includes(extension)) {
        return 'mdi-file-music'
      }

      // Text files
      if (['txt', 'md', 'json', 'xml', 'html', 'css', 'js'].includes(extension)) {
        return 'mdi-file-document-outline'
      }

      // Default file icon
      return '$yzi-file-02'
    },
    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 B'

      const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
      const i = Math.floor(Math.log(bytes) / Math.log(1024))

      return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
    },
    formatFilename(filename) {
      return formatFilename(filename, this.truncateLength)
    },
    getFileKey(file) {
      return file.fileId || file.filename || JSON.stringify(file).substring(0, 10)
    },
    async generatePdfThumbnailForFile(file) {
      try {
        const fileKey = this.getFileKey(file)
        const refName = `pdfThumbnail_${fileKey}`

        // Wait for next tick to ensure ref is available
        await this.$nextTick()

        const thumbnailRef = this.$refs[refName]
        if (!thumbnailRef || !thumbnailRef[0]) {
          return
        }

        const container = thumbnailRef[0]

        // Create a temporary document object for the utility function
        const tempDoc = {
          fileUrl: this.getImageSrc(file),
          thumbnailLoading: false,
          thumbnail: null
        }

        // Generate thumbnail using the utility function
        await generatePdfThumbnail(tempDoc, this.thumbnailSize, this.thumbnailSize)

        if (tempDoc.thumbnail) {
          // Create and insert the thumbnail image
          const img = document.createElement('img')
          img.src = tempDoc.thumbnail
          img.style.width = '100%'
          img.style.height = '100%'
          img.style.objectFit = 'cover'
          img.style.borderRadius = '4px'

          // Clear container and add thumbnail
          container.innerHTML = ''
          container.appendChild(img)

          // Store thumbnail in file object for future use
          if (file) {
            file.thumbnail = tempDoc.thumbnail
          }
        }
      } catch (error) {
        console.warn('Failed to generate PDF thumbnail:', error)

        // Show fallback PDF icon
        const fileKey = this.getFileKey(file)
        const refName = `pdfThumbnail_${fileKey}`
        const thumbnailRef = this.$refs[refName]

        if (thumbnailRef && thumbnailRef[0]) {
          const container = thumbnailRef[0]
          container.innerHTML = `
            <div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background-color: #f5f5f5; border-radius: 4px;">
              <i class="material-icons" style="font-size: ${this.iconSize}px; color: #666;">description</i>
            </div>
          `
        }
      }
    },
    viewFile(file) {
      if (this.isImage(file)) {
        // For images, show in dialog
        this.selectedFile = this.getImageSrc(file)
        this.showViewImageDialog = true
      } else {
        // For non-images, open in new tab
        const fileUrl = this.getImageSrc(file)
        if (fileUrl) {
          window.open(fileUrl, '_blank')
        }
      }
    },
    deleteFile(file) {
      this.fileToDelete = file
      this.showDeleteDialog = true
    },
    async confirmDelete() {
      if (!this.fileToDelete) return

      const file = this.fileToDelete
      this.isDeleting = true

      try {
        if (this.deleteMode === 'immediate') {
          // Immediate mode - deleteFromApi handles both hid and non-hid files
          await this.deleteFromApi(file)

          // Remove from v-model if delete succeeded (API or local)
          const updatedFiles = [...this.value]
          const fileIndex = updatedFiles.findIndex(f =>
            (f.fileId && f.fileId === file.fileId) ||
            (f.hid && f.hid === file.hid) ||
            f === file
          )

          if (fileIndex !== -1) {
            updatedFiles.splice(fileIndex, 1)
            this.$emit('input', updatedFiles)

            // Emit deleted items array
            this.$emit('deleted', [file])
          }
        } else {
          // Preview mode - add to toDeleteItems and remove from v-model
          const updatedFiles = [...this.value]
          const fileIndex = updatedFiles.findIndex(f =>
            (f.fileId && f.fileId === file.fileId) ||
            (f.hid && f.hid === file.hid) ||
            f === file
          )

          if (fileIndex !== -1) {
            // Remove from files array
            updatedFiles.splice(fileIndex, 1)
            this.$emit('input', updatedFiles)

            // Add to toDeleteItems if file has hid property
            if (file.hid) {
              const updatedToDeleteItems = [...this.toDeleteItems, file]
              this.$emit('update:toDeleteItems', updatedToDeleteItems)
            }

            // Emit deleted items array for preview mode
            this.$emit('deleted', [file])
          }
        }

        this.showDeleteDialog = false
        this.fileToDelete = null
      } catch (error) {
        // Delete failed, don't remove from UI
        console.error('Failed to delete file:', error)
      } finally {
        this.isDeleting = false
      }
    },
    cancelDelete() {
      this.showDeleteDialog = false
      this.fileToDelete = null
    },
    async deleteFromApi(file) {
      if (!file) {
        return Promise.reject(new Error('File is required for deletion'))
      }

      const fileId = file.fileId || file.filename || Date.now()

      return new Promise((resolve, reject) => {
        // Initialize delete tracking
        this.deletingFiles.set(fileId, { deleting: true })
        this.isDeleting = true
        this.$emit('delete-start', { file, fileId })

        // If file has no hid, it's a local file - resolve immediately without API call
        if (!file.hid) {
          this.deletingFiles.set(fileId, { deleting: false, completed: true })
          this.isDeleting = Array.from(this.deletingFiles.values()).some(f => f.deleting)
          this.$emit('delete-success', { file, fileId, response: null })
          resolve(null)
          return
        }

        const headers = {
          ...this.deleteHeaders
        }

        // Build delete URL using file hid
        const deleteUrl = this.deleteUrl.endsWith('/') ? `${this.deleteUrl}${file.hid}/` : `${this.deleteUrl}/${file.hid}`

        this.$axios({
          method: this.deleteMethod,
          url: deleteUrl,
          headers
        })
          .then(response => {
            this.deletingFiles.set(fileId, { deleting: false, completed: true })
            this.isDeleting = Array.from(this.deletingFiles.values()).some(f => f.deleting)
            this.$emit('delete-success', { file, fileId, response: response.data })
            resolve(response.data)
          })
          .catch(error => {
            this.deletingFiles.set(fileId, { deleting: false, error: true })
            this.isDeleting = Array.from(this.deletingFiles.values()).some(f => f.deleting)
            this.$emit('delete-error', { file, fileId, error })
            this.showMessage(['error', `Failed to delete file: ${error.message || 'Unknown error'}`])
            reject(error)
          })
      })
    }
  },
  beforeDestroy() {
    // Clean up blob URLs to prevent memory leaks
    if (this.value && Array.isArray(this.value)) {
      this.value.forEach(file => {
        if (file.file && file.file instanceof File && file.blob) {
          try {
            URL.revokeObjectURL(file.blob)
          } catch (e) {
            // Ignore errors
          }
        }
      })
    }
  }
}
</script>

<style scoped>
.yz-file-preview {
  width: 100%;
}

.yz-file-preview__pdf-thumbnail {
  overflow: hidden;
}

.yz-file-preview__icon-container {
  background-color: var(--yz-neutral-200, #f5f5f5);
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>